"use client";

import AnimatedLogoMark from "@/components/brand/AnimatedLogoMark";
import { useEffect, useRef, useState } from "react";
import Link from "@/lib/localized-link";
import { usePathname } from "next/navigation";
import { ChevronDown } from "lucide-react";
import { useLocalizedRouter } from "@/lib/localized-router";
import { useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { NAV_GROUPS, type NavGroupDef } from "@/lib/nav-groups";
import { enabledItems, useFeatures } from "@/lib/use-features";
import NewPill from "./NewPill";
import { useAnnouncements, useMarksNew } from "@/lib/use-announcements";
import LanguageToggle from "./LanguageToggle";
import { useLatestRelease } from "@/lib/use-releases";
import TokenBadge from "./TokenBadge";
import AuthButton from "./AuthButton";
import VerifyEmailBanner from "./VerifyEmailBanner";
import { useNavigationPending } from "@/lib/nav-pending";
import { NEW_DOT, ROW_ON } from "@/lib/ui-classes";

// The bar's groups come from lib/nav-groups.ts (NAV_GROUPS), the same
// definition the phone "More" sheet reads, so both list the same pages.

// The current section: a grey fill and dark text (quiet UI, 2026-10-29;
// Linear's active row), not an indigo tint.
const ACTIVE_CHIP = "bg-gray-100 text-gray-900";

/** Grouped nav item rendered as an APG "disclosure navigation menu with
 *  top-level links" hybrid: a real <Link> to the default sub-page PLUS an
 *  adjacent toggle <button> (aria-expanded/aria-controls) that reveals the
 *  dropdown. Opens on hover (with intent timing), on focus, and on click;
 *  closes on Escape, outside pointerdown, and focus leaving the group.
 *  Not role=menu - that would strip link semantics and force the roving-
 *  tabindex menu contract. */
function NavGroup({
  item,
  rawPathname,
  onNavClick,
}: {
  item: NavGroupDef;
  rawPathname: string;
  onNavClick?: (e: React.MouseEvent<HTMLAnchorElement>, href: string) => void;
}) {
  const t = useT();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelId = `navgrp-${item.href.replace(/\W+/g, "")}`;
  const flags = useFeatures();
  const allChildren = item.sections.flatMap((sec) => enabledItems(sec.items, flags));
  const { newFor, seeHref } = useAnnouncements();
  // "New" pills only for established signed-in people (lib/use-
  // announcements). The changelog entry (Company menu) carries a dot
  // while the newest release is unseen here, for everyone the release
  // is news to (lib/use-releases: never on a first visit, never for an
  // account the release predates, never past 7 days); opening the page
  // clears it. Since 2026-10-18 this dot is the whole announcement: the
  // corner card that covered content is gone (owner).
  const { dot: releaseDot } = useLatestRelease(user);
  const marksNew = useMarksNew();
  const newHere = (href: string) => marksNew && newFor(href).length > 0;
  const releaseEntry = (href: string) => href === "/whats-new" && releaseDot;
  const groupHasNew = allChildren.some((c) => newHere(c.href) || releaseEntry(c.href));

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };
  // Hover-intent: a short open delay, a longer close delay so crossing the
  // gap to the panel (or between adjacent triggers) doesn't flicker.
  const hoverOpen = () => {
    clearTimers();
    openTimer.current = setTimeout(() => setOpen(true), 120);
  };
  const hoverClose = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => setOpen(false), 280);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  // Clear any pending timers on unmount.
  useEffect(() => () => clearTimers(), []);

  // Parent lights up for the whole group (e.g. /calculate/mortgage lights
  // "Calculate"); the exact sub-page additionally gets aria-current.
  const parentActive = allChildren.some(
    (c) => rawPathname === c.href || (c.href !== "/" && rawPathname.startsWith(c.href + "/")),
  ) || rawPathname === item.href;

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={hoverOpen}
      onMouseLeave={hoverClose}
      onFocus={() => {
        clearTimers();
        setOpen(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      {/* `group` + group-hover: the hover highlight must treat the label
          link AND the chevron toggle as ONE chip - hovering anywhere in
          the chip darkens both texts, matching the plain (chevron-less)
          nav items. Per-child hover: classes alone left the chevron (and
          the label when the pointer sat on the chevron) undarkened, so
          the chip barely read as hovered. */}
      <div
        className={`group flex items-center rounded-lg transition-colors ${
          parentActive ? ACTIVE_CHIP : "hover:bg-gray-50"
        }`}
      >
        {/* The "something new inside" dot leads the label (owner, 2026-10-17);
            grey, and only for a week after the launch (quiet UI). */}
        {groupHasNew && (
          <span aria-hidden className={`ml-2.5 h-1.5 w-1.5 shrink-0 rounded-full ${NEW_DOT}`} />
        )}
        <Link
          href={item.href}
          onClick={(e) => onNavClick?.(e, item.href)}
          aria-current={rawPathname === item.href ? "page" : undefined}
          className={`${groupHasNew ? "pl-1.5" : "pl-3"} pr-1 py-1.5 text-sm font-medium transition-colors ${
            parentActive ? "" : "text-gray-500 group-hover:text-gray-700"
          }`}
        >
          {t(item.labelKey)}
        </Link>
        <button
          ref={toggleRef}
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={t(item.labelKey)}
          onClick={() => setOpen((o) => !o)}
          className={`pr-2 pl-0.5 py-1.5 transition-colors ${
            parentActive ? "" : "text-gray-400 group-hover:text-gray-700"
          }`}
        >
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
        </button>
      </div>
      {open && (
        <div
          id={panelId}
          className="absolute left-0 top-full z-50 mt-1 min-w-[230px] rounded-xl border border-gray-200 bg-white py-1.5 shadow-xl"
        >
          {item.sections.map((sec, si) => (
            <div key={si} className={si > 0 ? "mt-1 border-t border-gray-100 pt-1" : ""}>
              {sec.headingKey && (
                <p className="px-3 pb-1 pt-1.5 text-xs font-semibold text-gray-400">{t(sec.headingKey)}</p>
              )}
              <ul>
                {enabledItems(sec.items, flags).map((child) => {
                  const childActive = rawPathname === child.href;
                  const isNew = newHere(child.href);
                  return (
                    <li key={child.href}>
                      <Link
                        href={child.href}
                        aria-current={childActive ? "page" : undefined}
                        onClick={(e) => { setOpen(false); seeHref(child.href); onNavClick?.(e, child.href); }}
                        className={`flex items-center gap-2 px-3 py-2 text-sm ${
                          childActive ? ROW_ON : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span className="flex-1">{t(child.labelKey)}</span>
                        {isNew && <NewPill onSolid={childActive} />}
                        {!isNew && releaseEntry(child.href) && (
                          <>
                            <span aria-hidden className={`h-1.5 w-1.5 shrink-0 rounded-full ${NEW_DOT}`} />
                            <span className="sr-only">{t("wn.unseen")}</span>
                          </>
                        )}
                        {child.pro && (
                          <span className="rounded bg-amber-100 px-1 py-0.5 text-xs font-bold text-amber-700">Pro</span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SharedHeader() {
  const t = useT();
  // The mark turns while a page change is on its way (lib/nav-pending).
  const navPending = useNavigationPending();
  const pathname = usePathname();
  const rawPathname = pathname.replace(/^\/(az|ru|en)/, "") || "/";
  const { user, loading: authLoading } = useAuth();
  const router = useLocalizedRouter();

  // Direct /map -> /listings transition carries the live map filters so
  // the list shows the same subset the user just narrowed on the map.
  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (href !== "/listings" || !rawPathname.startsWith("/map")) return;
    const params = new URLSearchParams(window.location.search);
    params.delete("layer");
    params.delete("page");
    const qs = params.toString();
    if (qs) {
      e.preventDefault();
      router.push(`/listings?${qs}`);
    }
  };

  // Transparent at the top of the page, solid once scrolled - the hero
  // owns the first screen; the bar materialises only when it starts
  // covering content. rAF-free: scroll events are cheap at this
  // granularity and the state flips only at the 8px boundary.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // The header's rendered height as a CSS variable, for anything that
  // sticks under it (the hub's editing bar): the "confirm your email"
  // banner makes it taller than the 3.5rem the bars assumed.
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const set = () => document.documentElement.style.setProperty("--site-header-h", `${Math.round(el.getBoundingClientRect().height)}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => { ro.disconnect(); document.documentElement.style.removeProperty("--site-header-h"); };
  }, []);

  return (
    <header
      ref={headerRef}
      data-fullscreen-hide
      data-scrolled={scrolled ? "true" : "false"}
      // Own view-transition-name so tab-bar page slides move only the
      // content, never the chrome (see globals.css vt- block).
      style={{ viewTransitionName: "site-header" } as React.CSSProperties}
      className={`site-header sticky top-0 z-50 border-b transition-colors duration-200 pt-[env(safe-area-inset-top)] ${
        scrolled ? "border-gray-100 bg-white" : "border-transparent bg-transparent"
      }`}
    >
      <VerifyEmailBanner />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link href="/" className="flex items-center">
            {/* The mark whirls into a house on hover, focus or tap (the
                owner's animated logo, 2026-10-18); its geometry loads on
                the first touch, the static tile shows until then. */}
            <AnimatedLogoMark size={28} markClassName="h-6 w-6 sm:h-7 sm:w-7" active={navPending || undefined} loop={navPending}>
              <span className="text-xl font-extrabold text-gray-900" translate="no">
                {t("brand")}
              </span>
            </AnimatedLogoMark>
          </Link>
          <nav className="hidden sm:flex items-center gap-1">
            {NAV_GROUPS.map((item) => (
              <NavGroup key={item.key} item={item} rawPathname={rawPathname} onNavClick={handleNavClick} />
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          {/* Trial status lives in TokenBadge (it replaces the token
              count while a trial runs) - a second chip here doubled it. */}
          <TokenBadge />
          {!authLoading && !user && <LanguageToggle />}
          {/* Signed-out: the login button now rides the mobile bar too
              (right of the language picker) - the wordmark hides on
              mobile to make the width work. */}
          <AuthButton />
        </div>
      </div>
    </header>
  );
}
